// src/components/admin/AdminLayout.tsx
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Menu, X } from "lucide-react";

type NavItem = {
  label: string;
  to?: string;
  children?: NavItem[];
};

function envBool(key: string, defaultValue: boolean) {
  const v = (import.meta as any).env?.[key];
  if (v === undefined || v === null || v === "") return defaultValue;
  const s = String(v).trim().toLowerCase();
  return ["1", "true", "yes", "y", "on"].includes(s);
}

const POOM_MMS_URL = "https://mms.ezpgpayment.com/login";

export default function AdminLayout() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  // ✅ 메뉴 노출 플래그 (.env.production에서 제어 / Vite는 빌드 타임 반영)
  const SHOW_BOARDS = envBool("VITE_ADMIN_MENU_BOARDS", false); // 게시판 그룹 노출 여부
  const SHOW_FAQ = envBool("VITE_ADMIN_MENU_FAQ", true); // ✅ FAQ만 기본 ON
  const SHOW_GUIDES = envBool("VITE_ADMIN_MENU_GUIDES", false);
  const SHOW_NOTICES = envBool("VITE_ADMIN_MENU_NOTICES", false);
  const SHOW_QNA_CATEGORIES = envBool("VITE_ADMIN_MENU_QNA_CATEGORIES", false);

  const SHOW_SERVICE_CATEGORIES = envBool(
    "VITE_ADMIN_MENU_SERVICE_CATEGORIES",
    false
  ); // 첨부서류 안내(미완성이라 숨김)

  const NAV_SECTIONS: NavItem[] = useMemo(() => {
    // ✅ 게시판 하위 항목(토글로 개별 제어)
    const boardChildren: NavItem[] = [];
    if (SHOW_QNA_CATEGORIES)
      boardChildren.push({
        label: "질문 카테고리 관리",
        to: "/admin/boards/qna-categories",
      });
    if (SHOW_FAQ)
      boardChildren.push({
        label: "자주 묻는 질문 관리",
        to: "/admin/boards/faq",
      });
    if (SHOW_GUIDES)
      boardChildren.push({ label: "가이드 관리", to: "/admin/boards/guides" });
    if (SHOW_NOTICES)
      boardChildren.push({
        label: "공지사항 관리",
        to: "/admin/boards/notices",
      });

    const sections: NavItem[] = [
      {
        label: "회원 관리",
        children: [
          { label: "사용자", to: "/admin/users" },
          { label: "가맹점", to: "/admin/merchants" },
        ],
      },
      {
        label: "결제/서류",
        children: [{ label: "결제 요청서 리스트", to: "/admin/payments" }],
      },
      {
        label: "고객센터",
        children: [{ label: "1:1 문의", to: "/admin/inquiries" }],
      },

      // ✅ 게시판 관리 (그룹 토글 ON + 하위 1개 이상 있을 때만 노출)
      ...(SHOW_BOARDS && boardChildren.length > 0
        ? ([
            {
              label: "게시판 관리",
              children: boardChildren,
            },
          ] as NavItem[])
        : []),

      {
        label: "환경 설정",
        children: [
          { label: "화면 작업 중 설정", to: "/admin/settings/main" },
          { label: "AI 블로그 자동생성", to: "/admin/settings/ai-blog" },
          { label: "카드 무이자/할부", to: "/admin/settings/payments" },
          { label: "개인정보처리방침 관리", to: "/admin/settings/privacy" },
          { label: "이용약관 관리", to: "/admin/settings/terms" },
          { label: "배너 설정", to: "/admin/settings/banners" },
          ...(SHOW_SERVICE_CATEGORIES
            ? [{ label: "첨부서류 안내", to: "/admin/settings/service-categories" }]
            : []),
          { label: "홈 팝업 공지", to: "/admin/settings/home-notice" },
        ],
      },
      { label: "회사 정보", to: "/admin/company" },
    ];

    return sections
      .map((s) => {
        if (!s.children) return s;
        const kids = s.children.filter(Boolean);
        return { ...s, children: kids };
      })
      .filter((s) => !s.children || s.children.length > 0);
  }, [
    SHOW_BOARDS,
    SHOW_FAQ,
    SHOW_GUIDES,
    SHOW_NOTICES,
    SHOW_QNA_CATEGORIES,
    SHOW_SERVICE_CATEGORIES,
  ]);

  function SidebarPanel({ mobile }: { mobile?: boolean }) {
    return (
      <div className="flex h-full w-72 flex-col bg-white shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
              K
            </span>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-slate-900">
                JULYU PAY
              </span>
              <span className="text-xs text-slate-500">관리자</span>
            </div>
          </div>

          {mobile && (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700"
              onClick={() => setMobileOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV_SECTIONS.map((section) =>
            section.children ? (
              <div key={section.label} className="mb-4">
                <div className="mb-1 px-2 text-xs font-semibold text-slate-500">
                  {section.label}
                </div>
                <div className="space-y-1">
                  {section.children.map((item) => (
                    <SidebarLink key={item.label} to={item.to}>
                      {item.label}
                    </SidebarLink>
                  ))}
                </div>
              </div>
            ) : (
              <div key={section.label} className="mb-1">
                <SidebarLink to={section.to}>{section.label}</SidebarLink>
              </div>
            )
          )}
        </nav>

        {/* ✅ 요청: 사이드바 하단 외부 링크 버튼 (회사 정보 아래쪽 영역) */}
        <div className="px-3 pb-3">
          <a
            href={POOM_MMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
          >
            POOM 결제관리
          </a>
        </div>

        <div className="border-t px-3 py-2">
          <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-slate-600">관리자 전용 영역</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="flex h-14 items-center justify-between border-b bg-white px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-slate-900">
              관리자 페이지
            </span>
            <span className="text-[11px] text-slate-500">
              결제요청 승인 / 관리
            </span>
          </div>
        </div>

        <Link
          to="/"
          className="hidden sm:inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
        >
          사용자 화면으로 이동
        </Link>
      </header>

      <div className="flex flex-1">
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] lg:block">
          <SidebarPanel />
        </aside>

        {/* ✅ main에 min-w-0 추가(테이블/그리드 overflow 시 레이아웃 깨짐 방지) */}
        <main className="flex-1 min-w-0 bg-white">
          {/* ✅ PC에서 더 '꽉 차게' : max-w 확장 + 좌우 패딩 확장 */}
          <div className="w-full px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
            <div className="mx-auto w-full max-w-7xl xl:max-w-[1680px] 2xl:max-w-[1920px]">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full w-72 max-w-[80%] animate-slide-in-left">
            <SidebarPanel mobile />
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarLink({
  to,
  children,
}: {
  to?: string;
  children: React.ReactNode;
}) {
  if (!to) {
    return (
      <div className="rounded-lg px-3 py-2 text-sm text-slate-500">
        {children}
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "flex items-center justify-between rounded-lg px-3 py-2 text-sm",
          isActive
            ? "bg-indigo-50 font-medium text-indigo-700"
            : "text-slate-700 hover:bg-slate-50",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}
